// src/components/offline/OfflineOrdersList.js
import React from 'react';
import { Clock, CheckCircle, AlertTriangle, Upload, Trash2 } from 'lucide-react';
import { useOfflineStorage } from '../../hooks/useOfflineStorage';

const OfflineOrdersList = () => {
  const { 
    offlineOrders, 
    clearSyncedOrders, 
    syncPendingOrders,
    isOnline 
  } = useOfflineStorage();

  if (offlineOrders.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Nessun ordine offline
        </h3>
        <p className="text-gray-600">
          Gli ordini creati in modalità offline appariranno qui
        </p>
      </div>
    );
  }

  const syncedOrders = offlineOrders.filter(order => order.synced);
  const pendingOrders = offlineOrders.filter(order => !order.synced);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateOrderTotal = (order) => {
    if (!order.data.items) return 0;
    return order.data.items.reduce((total, item) => {
      return total + (item.quantity * item.unitPrice);
    }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Ordini in attesa di sincronizzazione */}
      {pendingOrders.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-500" />
              Ordini in attesa di sincronizzazione ({pendingOrders.length})
            </h3>
            {isOnline && (
              <button
                onClick={syncPendingOrders}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span>Sincronizza Tutti</span>
              </button>
            )}
          </div>
          
          <div className="space-y-3">
            {pendingOrders.map(order => (
              <div key={order.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900">
                        {order.data.customer?.companyName || 'Cliente sconosciuto'}
                      </h4>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                        Offline
                      </span>
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>
                        <span className="font-medium">Creato:</span> {formatTimestamp(order.timestamp)}
                      </p>
                      <p>
                        <span className="font-medium">Articoli:</span> {order.data.items?.length || 0}
                      </p>
                      <p>
                        <span className="font-medium">Totale:</span> € {calculateOrderTotal(order).toFixed(2)}
                      </p>
                      {order.retryCount > 0 && (
                        <p className="text-orange-600">
                          <span className="font-medium">Tentativi sync:</span> {order.retryCount}
                        </p>
                      )}
                    </div>

                    {/* Dettagli prodotti */}
                    {order.data.items && order.data.items.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">Prodotti ordinati:</p>
                        <div className="space-y-1">
                          {order.data.items.slice(0, 3).map((item, index) => (
                            <div key={index} className="flex justify-between text-xs text-gray-600">
                              <span>{item.product?.description || item.productId}</span>
                              <span>{item.quantity}x € {item.unitPrice?.toFixed(2)}</span>
                            </div>
                          ))}
                          {order.data.items.length > 3 && (
                            <p className="text-xs text-gray-500">
                              ...e altri {order.data.items.length - 3} prodotti
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    <span className="text-sm text-orange-700 font-medium">
                      {isOnline ? 'In attesa' : 'Offline'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ordini sincronizzati */}
      {syncedOrders.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
              Ordini sincronizzati ({syncedOrders.length})
            </h3>
            <button
              onClick={clearSyncedOrders}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm">Pulisci lista</span>
            </button>
          </div>
          
          <div className="space-y-3">
            {syncedOrders.map(order => (
              <div key={order.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900">
                        {order.data.customer?.companyName || 'Cliente sconosciuto'}
                      </h4>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        Sincronizzato
                      </span>
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>
                        <span className="font-medium">Creato offline:</span> {formatTimestamp(order.timestamp)}
                      </p>
                      {order.syncedAt && (
                        <p>
                          <span className="font-medium">Sincronizzato:</span> {formatTimestamp(order.syncedAt)}
                        </p>
                      )}
                      <p>
                        <span className="font-medium">Articoli:</span> {order.data.items?.length || 0}
                      </p>
                      <p>
                        <span className="font-medium">Totale:</span> € {calculateOrderTotal(order).toFixed(2)}
                      </p>
                      {order.serverOrderId && (
                        <p className="text-green-600">
                          <span className="font-medium">ID Server:</span> {order.serverOrderId}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-green-700 font-medium">
                      Completato
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistiche riepilogative */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Riepilogo</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{offlineOrders.length}</div>
            <div className="text-gray-600">Totale ordini</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{pendingOrders.length}</div>
            <div className="text-gray-600">Da sincronizzare</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{syncedOrders.length}</div>
            <div className="text-gray-600">Sincronizzati</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              € {offlineOrders.reduce((total, order) => total + calculateOrderTotal(order), 0).toFixed(0)}
            </div>
            <div className="text-gray-600">Valore totale</div>
          </div>
        </div>
      </div>

      {/* Suggerimenti per l'utente */}
      {!isOnline && pendingOrders.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Modalità Offline</h4>
              <p className="text-sm text-blue-800">
                Hai {pendingOrders.length} ordini in attesa di sincronizzazione. 
                Verranno inviati automaticamente quando tornerai online.
              </p>
            </div>
          </div>
        </div>
      )}

      {isOnline && pendingOrders.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h4 className="font-medium text-yellow-900 mb-1">Sincronizzazione Pendente</h4>
              <p className="text-sm text-yellow-800">
                Hai {pendingOrders.length} ordini da sincronizzare. 
                Clicca "Sincronizza Tutti" per inviarli al server.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineOrdersList;